import { refreshIdentity, useSession } from "@/features/auth";
import { setIdentityStaleHandler, setUnauthorizedHandler } from "@/shared/api-fetch";
import { router } from "./router";

/**
 * Ends the session the moment ANY request answers `STAFF_TOKEN_INVALID` — not just the one-shot
 * `features/auth/rehydrate.ts` runs once per page load.
 *
 * Before this file, an admin revoking a session mid-shift (or a token simply expiring) left the
 * header still showing the operator's name, role and Casino while every panel independently
 * reported its own generic "could not load" — `docs/UNOBSERVED.md` §1.5. Nothing said the session
 * was over, and recovery was a manual reload. Wired to `apiFetch`'s own hook
 * (`shared/api-fetch.ts`'s `setUnauthorizedHandler`) so a dead token is caught wherever it is next
 * used — a manager's report refetch, a technician's operation list, anything — rather than each
 * screen's own `classify()` guessing.
 *
 * **Preserves where the operator was**, the same property `router.ts`'s own guard gives an
 * unauthenticated visitor who hits a protected path directly, and the same one `login.vue` reads
 * back out via its `redirect` query param — a session that dies mid-shift should return the
 * operator to the same screen once they sign back in, not strand them at `HOME[role]`.
 * `reason: "session-expired"` is what tells `login.vue` to show an honest message instead of a
 * bare sign-in form.
 *
 * **Idempotent under concurrent 401s.** Several panels can be mid-refetch at once when a token
 * dies, each throwing its own `ApiError`. `session.logout()` clears `isAuthed` synchronously, so
 * by the time a second call to this handler runs, the guard below is already `false` and it is a
 * no-op — one navigation, not a race of several.
 *
 * **Never fires for an upstream failure.** `apiFetch` only calls the registered handler for
 * `STAFF_TOKEN_INVALID` specifically (see its own comment) — a 502 (`ApiError.isUpstream`) never
 * reaches this file at all, so a backend hiccup can never be mistaken for a dead session.
 */
function handleUnauthorized(): void {
  const session = useSession();
  if (!session.isAuthed) return;

  const current = router.currentRoute.value;
  session.logout();
  void router.replace({
    path: "/login",
    query: { redirect: current.fullPath, reason: "session-expired" },
  });
}

/**
 * A `ROLE_FORBIDDEN` or `STAFF_INACTIVE` from any request: re-ask `/v1/me` (CLAUDE.md, "re-fetch
 * after any surprising 403"). A changed role or Casino re-runs the route guard on the current
 * location (`force`), so a screen the new role cannot reach is left for its home rather than
 * rendered 403 by 403; a deactivated account ends the session exactly as a dead token does.
 */
async function handleIdentityStale(): Promise<void> {
  const outcome = await refreshIdentity();
  if (outcome === "inactive") {
    handleUnauthorized();
    return;
  }
  if (outcome === "changed") {
    const { path, query, hash } = router.currentRoute.value;
    await router.replace({ path, query, hash, force: true });
  }
}

/** Call once, at startup — mirrors `storage-sync.ts`'s `watchTokenAcrossTabs`. */
export function watchForExpiredSession(): void {
  setUnauthorizedHandler(handleUnauthorized);
  setIdentityStaleHandler(() => {
    void handleIdentityStale();
  });
}
